import {
  ActionBeginEventArgs,
  HtmlEditor,
  IFormatter,
  Inject,
  Link,
  NodeSelection,
  PasteCleanup,
  PasteCleanupSettingsModel,
  QuickToolbar,
  RichTextEditorComponent,
  Table,
  Toolbar,
} from '@fluentsms/syncfusion';
import React from 'react';

import SpecialCharactersDialog from './SpecialCharactersDialog';
import { handleUndoAction } from './helpers/undo.helper';

export enum ToolbarItems {
  BOLD = 'Bold',
  ITALIC = 'Italic',
  UNDERLINE = 'Underline',
  STRIKETHROUGH = 'StrikeThrough',
  FONTNAME = 'FontName',
  FONTSIZE = 'FontSize',
  FONTCOLOR = 'FontColor',
  BACKGROUNDCOLOR = 'BackgroundColor',
  LOWERCASE = 'LowerCase',
  UPPERCASE = 'UpperCase',
  FORMATS = 'Formats',
  ALIGNMENTS = 'Alignments',
  ORDEREDLIST = 'OrderedList',
  UNORDEREDLIST = 'UnorderedList',
  OUTDENT = 'Outdent',
  INDENT = 'Indent',
  SUPERSCRIPT = 'SuperScript',
  SUBSCRIPT = 'SubScript',
  CREATETABLE = 'CreateTable',
  CREATELINK = 'CreateLink',
  IMAGE = 'Image',
  CLEARFORMAT = 'ClearFormat',
  PRINT = 'Print',
  PREVIEW = 'SourceCode',
  FULLSCREEN = 'FullScreen',
  DIVIDER = '|',
  SPECIALCHARACTERS = 'SpecialCharacters',
  CHANGEHEIGHT = 'ChangeHeight',
  UNDO = 'Undo',
  REDO = 'Redo',
}

export interface CustomToolbarItem {
  template: string;
  undo: boolean;
  tooltipText: string;
}

const specialCharactersButton = {
  tooltipText: 'Special Characters',
  template: `
    <button class="e-tbar-btn e-btn special-chars-button" tabindex="-1" style="width:100%">
      <span class="e-tbar-btn-text" style="font-weight: 400; font-size: 22px">&#937;</span>
    </button>
  `,
};
const changeHeightButton = {
  template:
    '<button class="e-tbar-btn e-control e-btn e-lib e-icon-btn" type="button"  tabindex="-1" aria-label="Maximize" style="width: auto;"><span class="e-btn-icon e-maximize e-icons"></span></button>',
  undo: false,
  tooltipText: 'height',
};

const defaultItems = [
  ToolbarItems.BOLD,
  ToolbarItems.ITALIC,
  ToolbarItems.UNDERLINE,
  ToolbarItems.STRIKETHROUGH,
  ToolbarItems.SPECIALCHARACTERS,
  ToolbarItems.ALIGNMENTS,
  ToolbarItems.ORDEREDLIST,
  ToolbarItems.UNORDEREDLIST,
  ToolbarItems.OUTDENT,
  ToolbarItems.INDENT,
  ToolbarItems.SUPERSCRIPT,
  ToolbarItems.SUBSCRIPT,
  ToolbarItems.CREATETABLE,
  ToolbarItems.CREATELINK,
  // ToolbarItems.IMAGE,
  ToolbarItems.CHANGEHEIGHT,
  ToolbarItems.UNDO,
  ToolbarItems.REDO,
];

export interface RichTextEditorProps {
  /**
   * Boolean that determines if the toolbar should be overridden
   */
  customToolbar?: boolean;
  /**
   When customToolbar is true, sets the contents of the toolbar.
   */
  customToolbarContents?: (ToolbarItems | CustomToolbarItem)[];
  /**
   Optional number, sets the height of the rich text editor.
   If no height is set the rich text editor will scale with the text content.
   */
  height?: number;
  /**
   The prepopulated content of the editable text area
   */
  textContent?: string;
  /**
   Optional function passed in that will be triggered when component is created.
   */
  onCreated?: (element: HTMLElement | undefined) => void;
  /**
   Optional function to be passed in that fires on changes in the contents of the textArea, only fires when
   area is unfocused.  first string passed in is the most recent value of the text content of the editor
   second string is the html representation of the text
   */
  onChange?: (text: string, html: string, element: HTMLElement | undefined) => void;
  /**
   Optional function passed in that fires when the editor is blurred.  Text passed in is the most recent text content.
   HTML is the html representation of the text.
   */
  onBlur?: (text: string, html: string) => void;
  /**
   Optional function that fires when the editor is focused.  Text passed in is the most recent text content.
   HTML is the html representation of the text.
   */
  onFocus?: (text: string, html: string) => void;
  /**
   * Optional boolean that sets the editor as focused on creation
   */
  autoFocus?: boolean;
  /**
   * Optional boolean that enables the Tab Character inside the editor. Defaults to false
   */
  enableTabKey?: boolean;
  /**
   * Optional provide custom onPaste settings
   */
  pasteCleanupSettings?: PasteCleanupSettingsModel;
  /**
   * Optional function that returns the amount which the editor height should be changed when the CHANGEHEIGHT button is clicked on the toolbar
   */
  setHeight?: () => number;

  /**
   * Function that wraps merge fields in non-editable spans.  This function ensures that merge fields are restored correctly on undo.
   * @param {string} text the text content of the editor in HTML, stringified
   * @returns {string} the text content in html with the merge fields wrapped
   */
  wrapMergeFields?: (text: string) => string;
  /**
   * Optional boolean that will return xhtml on event calls
   */
  enableXhtml?: boolean;
  /**
   * Optional ID to pass to the editor.
   */
  editorId?: string;
}

export const defaultPasteSettings: PasteCleanupSettingsModel = {
  prompt: false,
  keepFormat: false,
  plainText: true,
  deniedAttrs: [],
  deniedTags: [],
  allowedStyleProps: [],
};

export class RichTextEditor extends React.Component<RichTextEditorProps> {
  private toolbarSettings: (
    | ToolbarItems
    | CustomToolbarItem
    | { tooltipText: string; template: string }
  )[] = defaultItems as ToolbarItems[];
  private textContent: string;
  private height: number | undefined;
  private pasteCleanupSettings: PasteCleanupSettingsModel = defaultPasteSettings;
  container: RichTextEditorComponent | undefined;
  maximizeButton: HTMLElement | undefined;
  specialCharacterButton: HTMLElement | undefined;
  selection: NodeSelection;
  enableTabKey: boolean;
  autoFocus: boolean;
  range: Range | undefined;
  saveSelection: NodeSelection | undefined;
  dialogInstance: SpecialCharactersDialog | undefined;

  constructor(props: RichTextEditorProps) {
    super(props);

    // this replaces the changeHeight icon enum (which corresponds to nothing) with the custom maximize button.
    if (props.customToolbar) {
      const temp = props.customToolbarContents ?? [];
      if (
        props.customToolbarContents &&
        props.customToolbarContents.find((element) => element === ToolbarItems.CHANGEHEIGHT) &&
        props.setHeight
      ) {
        const index = props.customToolbarContents.findIndex((element) => element === ToolbarItems.CHANGEHEIGHT);
        temp.splice(index, 1, changeHeightButton);
      }
      this.toolbarSettings = temp;
    } else {
      const temp = this.toolbarSettings;
      const index = this.toolbarSettings.findIndex((element) => element === ToolbarItems.CHANGEHEIGHT);
      if (index !== -1) {
        temp.splice(index, 1, changeHeightButton);

        this.toolbarSettings = temp;
      }
    }

    // Replace special characters enum with custom button
    const idx = this.toolbarSettings.findIndex((tool) => tool === ToolbarItems.SPECIALCHARACTERS);
    if (idx > -1) {
      this.toolbarSettings[idx] = specialCharactersButton;
    }

    this.textContent = props?.textContent ?? '';
    this.height = props?.height;
    this.selection = new NodeSelection();
    this.enableTabKey = props?.enableTabKey === undefined ? true : props.enableTabKey;
    this.autoFocus = props?.autoFocus === undefined ? true : props.autoFocus;

    if (props.pasteCleanupSettings) {
      this.pasteCleanupSettings = props.pasteCleanupSettings;
    }
  }

  private onEscapeKeyPress(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this.container?.focusOut();
    }
  }

  private saveCursorLocation(): void {
    try {
      this.range = this.selection.getRange(document);
      this.saveSelection = this.selection.save(this.range, document);
    } catch (err) {
      // pass
    }
  }

  componentDidMount(): void {
    document.addEventListener('keydown', this.onEscapeKeyPress.bind(this));
  }

  getTextContent(): string {
    return this.container?.getText() || '';
  }

  getTextContentHtml(): string {
    const element = this.container?.getContent()?.childNodes[0] as Element;
    return element?.innerHTML || '';
  }

  getRootElement(): HTMLElement {
    return (this.container && this.container.getRootElement()) as HTMLElement;
  }

  private handleBlur(): void {
    this.saveCursorLocation();
    this.container && this.props.onBlur && this.props.onBlur(this.container.getText(), this.container.getXhtml());
  }

  private handleChange(): void {
    this.container &&
      this.props.onChange &&
      this.props.onChange(this.container.getText(), this.container.getXhtml(), this.container?.getRootElement());
  }

  private handleFocus(): void {
    this.container && this.props.onFocus && this.props.onFocus(this.container.getText(), this.container.getXhtml());
  }

  private handleOnCreated(): void {
    this.props.onCreated && this.props.onCreated(this.container?.getRootElement());
    this.autoFocus && this.container && this.container.focusIn();

    // Get the toolbar button associated with the current instance of the RTE
    this.specialCharacterButton = this.container?.element.querySelector('.special-chars-button') as HTMLElement;
    if (this.specialCharacterButton) {
      this.specialCharacterButton.onclick = () => {
        this.saveCursorLocation();
        this.dialogInstance?.showDialog('0px');
      };
    }

    this.maximizeButton = this.container?.element.querySelector('.e-maximize') as HTMLElement;
    if (this.maximizeButton) {
      this.maximizeButton.onclick = () => {
        let newHeight;

        if (this.props.setHeight) {
          newHeight = this.props.setHeight();
        }

        if (!this.props.setHeight && this.container && this.container.height) {
          const temp = this.container.height as number;
          newHeight = temp + 100;
        }

        if (this.container && this.container.height) {
          this.container.height = newHeight as number;
          // update the instance var height to reflect height change
          this.height = this.container.height;
        }
      };
    }
  }

  private handleActionBegin(args: ActionBeginEventArgs | undefined): void {
    handleUndoAction(args, this.textContent, this.props.wrapMergeFields);

    if (args?.requestType === 'EnterAction') {
      args.cancel = true;
    }
  }

  shouldComponentUpdate(): boolean {
    return false;
  }

  insertSpecialCharacter(value: string): void {
    this.dialogInstance?.hideDialog();

    const rte: RichTextEditorComponent = this.container as RichTextEditorComponent;
    const formatter: IFormatter = rte.formatter as IFormatter;

    if (!formatter || !formatter.getUndoRedoStack || !formatter.saveData || !formatter.enableUndo) return;

    if (formatter?.getUndoRedoStack()?.length === 0) {
      formatter?.saveData();
    }

    this.saveSelection?.restore();
    rte.executeCommand('insertText', value);
    formatter.saveData();
    formatter.enableUndo(this.container);
  }

  insertHTML(value: string): void {
    const rte: RichTextEditorComponent = this.container as RichTextEditorComponent;
    const formatter: IFormatter = rte.formatter as IFormatter;

    if (!formatter || !formatter.getUndoRedoStack || !formatter.saveData || !formatter.enableUndo) return;

    if (formatter.getUndoRedoStack()?.length === 0) {
      formatter?.saveData();
    }

    this.saveSelection?.restore();
    rte.executeCommand('insertHTML', value);
    formatter.saveData();
    formatter.enableUndo(this.container);
  }

  render(): JSX.Element {
    return (
      <>
        <RichTextEditorComponent
          id={this.props.editorId ?? ''}
          data-testid="rich-text-editor"
          blur={this.handleBlur.bind(this)}
          focus={this.handleFocus.bind(this)}
          change={this.handleChange.bind(this)}
          actionBegin={this.handleActionBegin.bind(this)}
          ref={(scope) => (this.container = scope as RichTextEditorComponent)}
          toolbarSettings={{ items: this.toolbarSettings }}
          value={this.textContent}
          height={this.height}
          created={() => this.handleOnCreated()}
          enableTabKey={this.enableTabKey}
          pasteCleanupSettings={this.pasteCleanupSettings}
          undoRedoTimer={100}
          saveInterval={10}
          enableXhtml={this.props.enableXhtml}
        >
          <Inject services={[HtmlEditor, Link, QuickToolbar, Table, Toolbar, PasteCleanup]} />
        </RichTextEditorComponent>
        <SpecialCharactersDialog
          ref={(dialog) => (this.dialogInstance = dialog as SpecialCharactersDialog)}
          insertCharacter={(char) => this.insertSpecialCharacter(char)}
          target=".e-richtexteditor"
        />
      </>
    );
  }
}

export default RichTextEditor;
